import { apiRequest } from "../client";
import type {
  Report,
  CreateReportRequest,
  UpdateReportRequest,
  ReportLabel,
  ReportStylePreset,
  ReportStylePresetType,
  ChartPalettePayload,
  ListReportsResponse,
  Pagination,
  ListReportsSortBy,
  ListReportsSortOrder,
} from "./types";

const BASE_URL = "/report-builder";

/**
 * Creates a new report.
 * @param data - The data for the new report.
 * @returns The created report.
 */
export const createReport = async (
  data: CreateReportRequest
): Promise<Report> => {
  return apiRequest({
    method: "POST",
    url: BASE_URL,
    data,
  });
};

export type ListReportsParams = {
  /** Label IDs — AND semantics: report must contain every listed id */
  tags?: string[];
  /** Free-text search on name and description (1–200 chars after trim; omit if empty). */
  q?: string;
  /** 1-based page index (default 1 on server) */
  page?: number;
  page_size?: number;
  /** When omitted, the server default applies (`updatedAt` / `desc`). */
  sort_by?: ListReportsSortBy;
  sort_order?: ListReportsSortOrder;
};

/**
 * Lists reports for the user's company (paginated).
 * @param params - Optional `tags`, `q`, `page`, and `page_size` (max 200 on server).
 */
export const listReports = async (
  params?: ListReportsParams,
): Promise<ListReportsResponse> => {
  const tags = params?.tags?.filter((id) => id && String(id).trim() !== "");
  const page = params?.page ?? 1;
  const pageSize = params?.page_size ?? 50;
  const qTrimmed = params?.q?.trim() ?? "";
  const q =
    qTrimmed.length >= 1 && qTrimmed.length <= 200 ? qTrimmed : undefined;

  const search = new URLSearchParams();
  if (tags && tags.length > 0) {
    search.set("tags", tags.join(","));
  }
  if (q) {
    search.set("q", q);
  }
  search.set("page", String(page));
  search.set("page_size", String(pageSize));
  if (params?.sort_by) {
    search.set("sort_by", params.sort_by);
    search.set("sort_order", params.sort_order ?? "desc");
  }

  const res = await apiRequest<ListReportsResponse>({
    method: "GET",
    url: `${BASE_URL}?${search.toString()}`,
  });

  const reports = res.reports ?? [];
  const pagination: Pagination =
    res.pagination ??
    ({
      page,
      page_size: pageSize,
      total_items: reports.length,
      total_pages: Math.max(1, Math.ceil(reports.length / pageSize) || 1),
    } satisfies Pagination);

  return { reports, pagination };
};

/**
 * Fetches every report page (page_size 200) for flows that need the full list
 * (e.g. entity pickers). Prefer {@link listReports} when paginated UI is enough.
 */
export const listAllReports = async (
  params?: Omit<ListReportsParams, "page" | "page_size">,
): Promise<Report[]> => {
  const pageSize = 200;
  const all: Report[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await listReports({ ...params, page, page_size: pageSize });
    all.push(...res.reports);
    totalPages = res.pagination.total_pages;
    page++;
  } while (page <= totalPages);
  return all;
};

/**
 * Lists managed labels for the company (sorted by name on the server).
 */
export const listLabels = async (): Promise<ReportLabel[]> => {
  const res = await apiRequest<{ labels: ReportLabel[] }>({
    method: "GET",
    url: `${BASE_URL}/labels`,
  });
  return res.labels ?? [];
};

/**
 * Creates a managed label.
 */
export const createLabel = async (
  name: string,
  color?: string | null,
): Promise<ReportLabel> => {
  const data: { name: string; color?: string | null } = { name };
  if (color !== undefined && color !== null && color.trim() !== "") {
    data.color = color;
  }
  return apiRequest({
    method: "POST",
    url: `${BASE_URL}/labels`,
    data,
  });
};

/**
 * Updates a managed label (name and/or color).
 */
export const updateLabel = async (
  labelId: string,
  patch: { name?: string; color?: string | null },
): Promise<ReportLabel> => {
  return apiRequest({
    method: "PUT",
    url: `${BASE_URL}/labels/${encodeURIComponent(labelId)}`,
    data: patch,
  });
};

/**
 * Soft-deletes a label and removes it from all reports in the company.
 */
export const deleteLabel = async (
  labelId: string,
): Promise<{ success: boolean }> => {
  return apiRequest({
    method: "DELETE",
    url: `${BASE_URL}/labels/${encodeURIComponent(labelId)}`,
  });
};

/**
 * Lists managed style presets for the company (sorted by name on the server).
 */
export const listStylePresets = async (
  type?: ReportStylePresetType,
): Promise<ReportStylePreset[]> => {
  const params = new URLSearchParams();
  if (type) {
    params.set("type", type);
  }
  const qs = params.toString();
  const res = await apiRequest<{ presets: ReportStylePreset[] }>({
    method: "GET",
    url: `${BASE_URL}/presets${qs ? `?${qs}` : ""}`,
  });
  return res.presets ?? [];
};

/**
 * Creates a managed style preset.
 */
export const createStylePreset = async (body: {
  type: ReportStylePresetType;
  name: string;
  payload: ChartPalettePayload;
}): Promise<ReportStylePreset> => {
  return apiRequest({
    method: "POST",
    url: `${BASE_URL}/presets`,
    data: body,
  });
};

/**
 * Updates a managed style preset (name and/or payload).
 */
export const updateStylePreset = async (
  presetId: string,
  patch: { name?: string; payload?: ChartPalettePayload },
): Promise<ReportStylePreset> => {
  return apiRequest({
    method: "PUT",
    url: `${BASE_URL}/presets/${encodeURIComponent(presetId)}`,
    data: patch,
  });
};

/**
 * Soft-deletes a style preset.
 */
export const deleteStylePreset = async (
  presetId: string,
): Promise<{ success: boolean }> => {
  return apiRequest({
    method: "DELETE",
    url: `${BASE_URL}/presets/${encodeURIComponent(presetId)}`,
  });
};

/**
 * Retrieves a single report by its ID.
 * @param reportId - The ID of the report to retrieve.
 * @returns The requested report.
 */
export const getReport = async (reportId: string): Promise<Report> => {
  return apiRequest({
    method: "GET",
    url: `${BASE_URL}/${reportId}`,
  });
};

/**
 * Updates an existing report.
 * @param reportId - The ID of the report to update.
 * @param data - The update data.
 * @returns The updated report.
 */
export const updateReport = async (
  reportId: string,
  data: UpdateReportRequest
): Promise<Report> => {
  return apiRequest({
    method: "PUT",
    url: `${BASE_URL}/${reportId}`,
    data,
  });
};

/**
 * Clones an existing report.
 * @param reportId - The ID of the report to clone.
 * @returns The newly created report.
 */
export const cloneReport = async (reportId: string): Promise<Report> => {
  return apiRequest({
    method: "POST",
    url: `${BASE_URL}/${reportId}/clone`,
  });
};

/**
 * Deletes a report.
 * @param reportId - The ID of the report to delete.
 * @returns A success flag.
 */
export const deleteReport = async (
  reportId: string
): Promise<{ success: boolean }> => {
  return apiRequest({
    method: "DELETE",
    url: `${BASE_URL}/${reportId}`,
  });
};

/**
 * Toggles the favourite status of a report at company level.
 * @param reportId - The ID of the report to toggle.
 * @returns The updated report with new favourite status.
 */
export const toggleFavourite = async (reportId: string): Promise<Report> => {
  return apiRequest({
    method: "PUT",
    url: `${BASE_URL}/${reportId}/favourite`,
  });
};

/**
 * Custom chart component type returned from the API.
 * These are client-specific chart types that may be filtered based on company.
 */
export interface ReportCustomComponent {
  /** Unique identifier matching chartType in report configs (e.g., "legend-only", "risk-scale") */
  type: string;
  /** Display label for UI */
  label: string;
  /** Optional tooltip/help text */
  description?: string;
  /** Category for UI organization (e.g., "other") */
  group: string;
  /** Sort order within the group */
  sortOrder: number;
  /** Optional icon identifier */
  icon?: string | null;
}

/**
 * Response from the custom chart types endpoint.
 */
export interface ReportCustomComponentsResponse {
  chartTypes: ReportCustomComponent[];
}

/**
 * Fetches custom chart types available to the authenticated user's company.
 * Components are filtered based on client_id - "public" components are available
 * to all, while company-specific ones are only returned for matching users.
 *
 * @returns Custom chart types available to the user
 */
export const fetchCustomChartTypes =
  async (): Promise<ReportCustomComponentsResponse> => {
    return apiRequest({
      method: "GET",
      url: `${BASE_URL}/custom-components`,
    });
  };
